import type { GenderData, DecibelTableColumn, DateRange, ShowFilter } from '@/types';
import type { GenderPlaysResponse } from '@/types/api/gender';
import { uppercaseFirstLetter } from '@/utils';
import { Button, DataTable, Loader } from '@acast-tech/decibel';
import { ArrowDownToLineLightGlyph, ChevronRightRegularGlyph } from '@acast-tech/decibel-glyphs';
import Modal from '@/components/modals/Modal/Modal';
import { useGenderPlaysQuery } from '@/services/revenueApi';
import { useDerivedNetworkContext } from '@/context';
import { useFlags } from 'launchdarkly-react-client-sdk';
import NoDataMessage from '@/components/states/NoDataMessage';
import useGenderCsvExport from './useGenderCsvExport';
import {
  trackAudienceGenderModalDownloadClicked,
  trackAudienceGenderModalOpened,
} from './audienceModalMixpanel';

type GenderRow = { key: string; gender: string; [platform: string]: string | number };

const buildGenderTableData = (plays: GenderPlaysResponse['plays']): GenderRow[] => {
  const genderMap: Record<string, Record<string, number>> = {};

  plays.forEach(({ platform, gender_distribution }) => {
    gender_distribution.forEach(({ label, percentage }: GenderData) => {
      if (!genderMap[label]) genderMap[label] = {};
      genderMap[label][platform] = percentage;
    });
  });

  return Object.entries(genderMap).map(([gender, platformValues]) => ({
    key: gender,
    gender,
    ...platformValues,
  }));
};

const buildGenderTableColumns = (
  plays: GenderPlaysResponse['plays']
): DecibelTableColumn<GenderRow>[] => [
  {
    id: 'gender',
    title: '',
    render: (row) => uppercaseFirstLetter(row.gender as string),
  },
  ...plays.map(({ platform }) => ({
    id: platform,
    title: uppercaseFirstLetter(platform.replace(/_/g, ' ')),
    render: (row: GenderRow) => (row[platform] != null ? `${row[platform]}%` : '—'),
    align: 'right' as const,
  })),
];

const GenderModalContent = ({
  dateRange,
  selectedShow,
}: {
  dateRange: DateRange;
  selectedShow: ShowFilter;
}) => {
  const { selectedNetwork } = useDerivedNetworkContext();
  const { audienceSources } = useFlags<{ audienceSources?: string[] }>();
  const sources = audienceSources ?? ['acast', 'phyllo'];
  const showIds = selectedShow !== 'all' && selectedShow ? [selectedShow] : undefined;

  const { data, isPending } = useGenderPlaysQuery(
    selectedNetwork.id,
    dateRange.from,
    dateRange.to,
    undefined,
    { showIds, sources }
  );

  if (isPending) {
    return (
      <section className="px-6 py-10 flex flex-col justify-center items-center h-[60vh] gap-10">
        <Loader data-testid="loader" />
      </section>
    );
  }

  if (!data || data.plays.length < 1) {
    return <NoDataMessage dataType="gender data" />;
  }

  const tableData = buildGenderTableData(data.plays);
  const columns = buildGenderTableColumns(data.plays);

  return (
    <section className="px-6 py-10 flex flex-col gap-10">
      <DataTable
        title="gender"
        columns={columns}
        data={tableData}
        pagination={false}
        getRowId={(row) => row.key}
      />
    </section>
  );
};

const GenderModal = () => {
  const { handleCSVExport } = useGenderCsvExport();

  return (
    <Modal
      hashProp="gender-details"
      title="Gender details"
      showDateRangeFilter
      onOpen={trackAudienceGenderModalOpened}
      downloadConfig={{
        onDownload: (...args) => {
          trackAudienceGenderModalDownloadClicked();
          return handleCSVExport(...args);
        },
        downloadButtonText: 'Export as CSV',
        downloadButtonIcon: ArrowDownToLineLightGlyph,
      }}
      renderTrigger={(openModal) => (
        <div className="flex flex-row justify-end self-end">
          <Button
            className="self-right"
            variant="tertiary"
            glyphEnd={ChevronRightRegularGlyph}
            onClick={openModal}
          >
            More details
          </Button>
        </div>
      )}
    >
      {(dateRange, selectedShow) => (
        <GenderModalContent dateRange={dateRange} selectedShow={selectedShow} />
      )}
    </Modal>
  );
};

export default GenderModal;
